/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Container } from '@material-ui/core';
import ShopOnlineButton from 'components/ShopOnlineButton';
import { useTheme } from 'emotion-theming';
import EmbeddedContinueButton from 'features/EmbeddedContinueButton';
import IovationButton from 'features/IovationButton';
import { Theme } from 'features/Theme';
import useTranslate from 'hooks/useTranslate';
import { useMarkdownMultilineTranslate } from 'hooks/useTranslate/useTranslate';
import isEmpty from 'ramda/src/isEmpty';
import React, { Fragment } from 'react';
import { InstantCredit } from 'state/instantCredit';
import { Tenant } from 'state/tenant';
import Rewards from './Rewards';
import TemporaryWallets from './TemporaryWallets';
import PermanentWallets from './PermanentWallets';
import styles from './styles';
import TwoWayHybridButton from 'components/TwoWayHybridButton';
import FinancingWallets from './FinancingWallets';
import DemoWallets from './DemoWallets';

interface PropTypes {
  checkoutApproval?: boolean;
  tenant: Tenant;
  instantCredit: InstantCredit;
  storeNumber: string | null;
  onTextToRecoverClick: () => void;
  onTextToRecoverFinancingClick: () => void;
  sendingTextToRecover: boolean;
}
const CallToAction: React.FC<PropTypes> = props => {
  const theme = useTheme<Theme>();
  const t = useTranslate();

  const tMdMultiline = useMarkdownMultilineTranslate();

  const {
    checkoutApproval,
    tenant,
    instantCredit,
    storeNumber,
    onTextToRecoverClick,
    onTextToRecoverFinancingClick,
    sendingTextToRecover,
  } = props;

  const {
    enabledFeatures: {
      textToRecover,
      rewards: rewardsFeature,
      twoWayHybridApplyEcho,
      wallet,
      financingWallet,
      demoWallets,
    },
    tspDisplay: { textToRecoverAsPrimaryButton },
  } = tenant;

  const { rewards, virtualCardAccountNumber } = instantCredit;

  const hasRewards =
    rewardsFeature && rewards && rewards.certificates.length > 0;

  const displayTextToRecover = textToRecover;

  const shopOnlineUrl = t('dsp.offer.shopOnlineUrl');
  const hasTwoWayApplyHybridEcho =
    twoWayHybridApplyEcho && !isEmpty(twoWayHybridApplyEcho);
  const displayShopOnlineButton = Boolean(
    !theme.embedded && (hasTwoWayApplyHybridEcho || shopOnlineUrl),
  );

  let primaryCallToAction = null;
  let primaryCallToActionType:
    | 'shopNow'
    | 'textToRecover'
    | 'continue'
    | null = null;

  if (hasRewards && theme.embedded) {
    // Rewards include its own Continue button
    primaryCallToActionType = 'continue';
    primaryCallToAction = null;
  } else if (displayTextToRecover && textToRecoverAsPrimaryButton) {
    primaryCallToActionType = 'textToRecover';
    primaryCallToAction = (
      <Container maxWidth="sm">
        {checkoutApproval ? (
          <React.Fragment>
            <IovationButton
              onClick={onTextToRecoverFinancingClick}
              loading={sendingTextToRecover}
              variant="text"
              aria-label="Text To Recover Financing Button"
            >
              {t('dsp.offer.saveToMobileFinancing')}
            </IovationButton>
            <div css={styles.finePrint}>
              {tMdMultiline('dsp.offer.financingFinePrint')}
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <IovationButton
              onClick={onTextToRecoverClick}
              loading={sendingTextToRecover}
              variant="text"
              aria-label="Text To Recover Button"
            >
              {t('dsp.offer.saveToMobile')}
            </IovationButton>
            <div css={styles.finePrint}>
              {tMdMultiline('dsp.offer.finePrint')}
            </div>
          </React.Fragment>
        )}
      </Container>
    );
  } else if (theme.embedded && !hasRewards) {
    // In embedded, rewards contain the Submit button that will continue
    // If we don't have rewards, we need to display the continue button.
    primaryCallToActionType = 'continue';
    primaryCallToAction = <EmbeddedContinueButton variant="contained" />;
  } else if (!theme.embedded && displayShopOnlineButton) {
    primaryCallToActionType = 'shopNow';
    primaryCallToAction = hasTwoWayApplyHybridEcho ? (
      <TwoWayHybridButton variant="contained" />
    ) : (
      <ShopOnlineButton instantCredit={instantCredit} variant="contained" />
    );
  }

  return (
    <Fragment>
      {/*
       * Contained or image buttons in this section should be wrapped
       * in with the containedButton class so that we can set their
       * spacing looks consistent when mixed with text buttons.
       */}
      {rewardsFeature && rewards && rewards.certificates && (
        <Container maxWidth="xs">
          <Rewards rewards={rewards} rewardsFeature={rewardsFeature} />
        </Container>
      )}

      {primaryCallToAction && (
        <div css={styles.containedButton}>{primaryCallToAction}</div>
      )}

      {theme.embedded && primaryCallToActionType !== 'continue' && (
        <EmbeddedContinueButton />
      )}
      {demoWallets && (
        <DemoWallets
          buttonContainerStyle={styles.containedButton}
          containerStyles={styles.walletButtonsContainer}
        />
      )}
      {!demoWallets && checkoutApproval && financingWallet && (
        <FinancingWallets
          buttonContainerStyle={styles.containedButton}
          containerStyles={styles.walletButtonsContainer}
        />
      )}
      {!demoWallets &&
        !checkoutApproval &&
        wallet &&
        (wallet.type === 'accountNumberPass' ||
          wallet.type === 'callIdPass') && (
          <TemporaryWallets
            virtualCardAccountNumber={virtualCardAccountNumber}
            storeNumber={storeNumber}
            buttonContainerStyle={styles.containedButton}
            containerStyles={styles.walletButtonsContainer}
          />
        )}

      {!demoWallets &&
        !checkoutApproval &&
        wallet &&
        wallet.type === 'permanentCard' && (
          <PermanentWallets
            containerStyles={styles.walletButtonsContainer}
            buttonContainerStyle={styles.containedButton}
          />
        )}

      {/* Text to recover doesn't apply to DSP Push */}
      {displayTextToRecover && primaryCallToActionType !== 'textToRecover' && (
        <Container maxWidth="sm">
          {checkoutApproval ? (
            <React.Fragment>
              <IovationButton
                onClick={onTextToRecoverFinancingClick}
                loading={sendingTextToRecover}
                variant="text"
                aria-label="Text To Recover Financing Button"
              >
                {t('dsp.offer.saveToMobileFinancing')}
              </IovationButton>
              <div css={styles.finePrint}>
                {tMdMultiline('dsp.offer.financingFinePrint')}
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <IovationButton
                onClick={onTextToRecoverClick}
                loading={sendingTextToRecover}
                variant="text"
                aria-label="Text To Recover Button"
              >
                {t('dsp.offer.saveToMobile')}
              </IovationButton>
              <div css={styles.finePrint}>
                {tMdMultiline('dsp.offer.finePrint')}
              </div>
            </React.Fragment>
          )}
        </Container>
      )}

      {displayShopOnlineButton && primaryCallToActionType !== 'shopNow' && (
        <div>
          {hasTwoWayApplyHybridEcho ? (
            <TwoWayHybridButton variant="contained" />
          ) : (
            <ShopOnlineButton instantCredit={instantCredit} variant="text" />
          )}
        </div>
      )}
    </Fragment>
  );
};

export default CallToAction;
